import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../../components/ui/Badge/Badge';
import { Table } from '../../components/ui/Table';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';

export function InvitesList() {
  const [loading, setLoading] = React.useState(false);
  const [invites, setInvites] = React.useState<Array<any>>([]);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Get all organizations first, then fetch invites for each
        const orgsRes = await fetch('/api/admin/orgs', { credentials: 'include' });
        const orgsData = await orgsRes.json();
        
        if (!orgsData.success) throw new Error('Failed to load organizations');
        
        const orgs = orgsData.data || [];
        const allInvites = [];
        
        // Fetch invites for each organization
        for (const org of orgs) {
          try {
            const invitesRes = await fetch(`/api/admin/orgs/${org.id}/invites`, { credentials: 'include' });
            const invitesData = await invitesRes.json();
            if (invitesData.success) {
              const orgInvites = invitesData.data.map((invite: any) => ({
                ...invite,
                organizationName: org.name,
                organizationId: org.id
              }));
              allInvites.push(...orgInvites);
            }
          } catch (e) {
            console.error(`Failed to fetch invites for org ${org.id}:`, e);
          }
        }
        
        setInvites(allInvites);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const columns = [
    {
      key: 'email',
      title: 'Email',
      sortable: true
    },
    {
      key: 'organizationName',
      title: 'Organization',
      sortable: true,
      render: (value: string, record: any) => (
        <Link 
          to={`/admin/orgs/${record.organizationId}`}
          className="text-primary-600 hover:text-primary-800 hover:underline"
        >
          {value}
        </Link>
      )
    },
    {
      key: 'role',
      title: 'Role',
      render: (value: string) => <Badge variant="outline">{value}</Badge>
    },
    {
      key: 'status',
      title: 'Status',
      render: (_: any, record: any) => {
        const isAccepted = record.accepted_at;
        const isExpired = new Date(record.expires_at) < new Date();
        
        if (isAccepted) {
          return <Badge variant="success">Accepted</Badge>;
        } else if (isExpired) {
          return <Badge variant="error">Expired</Badge>;
        } else {
          return <Badge variant="warning">Pending</Badge>;
        }
      }
    },
    {
      key: 'created_at',
      title: 'Sent',
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{date.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{date.toLocaleTimeString()}</div>
          </div>
        );
      }
    },
    {
      key: 'expires_at',
      title: 'Expires',
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        const isExpired = date < new Date();
        return (
          <div className={isExpired ? 'text-red-600' : ''}>
            <div className="text-sm">{date.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{date.toLocaleTimeString()}</div>
          </div>
        );
      }
    }
  ];

  const filters = [
    {
      key: 'status',
      label: 'Filter by Status',
      options: [
        { label: 'Pending', value: 'pending' },
        { label: 'Accepted', value: 'accepted' },
        { label: 'Expired', value: 'expired' }
      ]
    },
    {
      key: 'role',
      label: 'Filter by Role',
      options: [
        { label: 'Owner', value: 'owner' },
        { label: 'Admin', value: 'admin' },
        { label: 'Member', value: 'member' }
      ]
    }
  ];

  // Process data for filtering
  const processedInvites = invites.map(invite => {
    const isAccepted = invite.accepted_at;
    const isExpired = new Date(invite.expires_at) < new Date();
    
    let status = 'pending';
    if (isAccepted) {
      status = 'accepted';
    } else if (isExpired) {
      status = 'expired';
    }
    
    return { ...invite, status };
  });

  return (
    <PageContainer>
      <PageHeader
        title="All Invitations"
        description="Manage invitations across all organizations"
      />
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      <Table
        data={processedInvites}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search invitations by email..."
        filters={filters}
        pagination={{
          pageSize: 15,
          showSizeChanger: true,
          pageSizeOptions: [10, 15, 25, 50]
        }}
        emptyText="No invitations found."
      />
    </PageContainer>
  );
}
